import type {
  ClaimsVolumeTrendResponse,
  DataConfidenceResponse,
  DashboardResponse,
  IdentityContext,
  MetricValue,
  SafetyReviewResponse,
  TenantAccess,
  TenantContext,
  Top5Response,
} from "@broadview/contracts";
import {
  claimsVolumeTrendResponseSchema,
  dataConfidenceResponseSchema,
  safetyReviewResponseSchema,
  top5ResponseSchema,
} from "@broadview/contracts";
import { clientError } from "./errors";
import {
  createBroadviewFixtureDataset,
  type FixtureDataset,
  type FixtureProblemRecord,
} from "./fixtures";
import { createLiveClient } from "./live";
import type { BroadviewFetch } from "./transport";

export type BroadviewClientMode = "fixture" | "live";

export type BroadviewClientOptions = Readonly<{
  mode?: BroadviewClientMode;
  fixtures?: FixtureDataset;
  apiOrigin?: string;
  getAccessToken?: () => Promise<string | undefined>;
  fetchImpl?: BroadviewFetch;
}>;

export type TenantScopedRequest = Readonly<{ tenantId: string }>;

export type DashboardRequest = TenantScopedRequest &
  Readonly<{
    view: string;
  }>;

export type MetricRequest = TenantScopedRequest &
  Readonly<{
    definitionId: string;
  }>;

export type BroadviewApiClient = Readonly<{
  mode: BroadviewClientMode;
  identity: Readonly<{
    me(): Promise<IdentityContext>;
    listTenants(): Promise<readonly TenantAccess[]>;
    getTenantContext(tenantId: string): Promise<TenantContext>;
  }>;
  dashboard: Readonly<{
    get(request: DashboardRequest): Promise<DashboardResponse>;
    getMetric(request: MetricRequest): Promise<MetricValue>;
  }>;
  claimsVolumeTrend: Readonly<{
    get(
      request: TenantScopedRequest &
        Readonly<{ sourceChoice?: import("@broadview/contracts").ClaimsVolumeSourceChoice }>,
    ): Promise<ClaimsVolumeTrendResponse>;
  }>;
  dataConfidence: Readonly<{
    get(request: TenantScopedRequest): Promise<DataConfidenceResponse>;
  }>;
  top5: Readonly<{
    get(request: TenantScopedRequest): Promise<Top5Response>;
  }>;
  safetyReview: Readonly<{
    get(request: TenantScopedRequest): Promise<SafetyReviewResponse>;
  }>;
  problemRecords: Readonly<{
    list(request: TenantScopedRequest): Promise<readonly FixtureProblemRecord[]>;
    get(request: TenantScopedRequest & Readonly<{ id: string }>): Promise<FixtureProblemRecord>;
  }>;
}>;

function requireTenant(dataset: FixtureDataset, tenantId: string): TenantContext {
  const context = dataset.tenantContexts.find((candidate) => candidate.tenantId === tenantId);
  if (!context) {
    throw clientError(404, "NOT_FOUND", "The requested tenant context is unavailable.");
  }
  return context;
}

function createFixtureClient(dataset: FixtureDataset): BroadviewApiClient {
  return {
    mode: "fixture",
    identity: {
      async me() {
        return dataset.identity;
      },
      async listTenants() {
        return dataset.identity.tenants;
      },
      async getTenantContext(tenantId) {
        return requireTenant(dataset, tenantId);
      },
    },
    dashboard: {
      async get({ tenantId, view }) {
        requireTenant(dataset, tenantId);
        const result = dataset.dashboards.find(
          (candidate) => candidate.tenantId === tenantId && candidate.view === view,
        );
        if (!result) {
          throw clientError(404, "NOT_FOUND", "The requested dashboard fixture is unavailable.");
        }
        return result;
      },
      async getMetric({ tenantId, definitionId }) {
        requireTenant(dataset, tenantId);
        const result = dataset.metrics.find(
          (candidate) => candidate.tenantId === tenantId && candidate.definitionId === definitionId,
        );
        if (!result) {
          throw clientError(404, "NOT_FOUND", "The requested metric fixture is unavailable.");
        }
        return result;
      },
    },
    claimsVolumeTrend: {
      async get({ tenantId }) {
        requireTenant(dataset, tenantId);
        const generatedAt =
          dataset.dashboards.find((dashboard) => dashboard.tenantId === tenantId)?.generatedAt ??
          new Date(0).toISOString();
        const unavailable = (metricId: "K002" | "K003" | "K004" | "K005") => ({
          metricId,
          definitionVersion: "UNACTIVATED",
          label: metricId,
          status: "UNAVAILABLE" as const,
          points: [],
          dataState: "UNAVAILABLE" as const,
          authorityClass: "UNAVAILABLE",
          productionUseProhibited: false,
          sourceSummary:
            "The browser fixture client does not simulate governed canonical claim records.",
          freshness: "UNKNOWN" as const,
          quality: "INSUFFICIENT" as const,
          reconciliation: "UNKNOWN" as const,
          permissions: ["metric.read"],
          provenance: [],
          unavailableReason:
            "The browser fixture client does not simulate governed canonical claim records.",
        });
        return claimsVolumeTrendResponseSchema.parse({
          view: "claims-volume-trend",
          schemaVersion: "1.0",
          tenantId,
          generatedAt,
          dataAsOf: generatedAt,
          series: [
            unavailable("K002"),
            unavailable("K003"),
            unavailable("K004"),
            unavailable("K005"),
          ],
          warnings: [],
        });
      },
    },
    dataConfidence: {
      async get({ tenantId }) {
        requireTenant(dataset, tenantId);
        const generatedAt =
          dataset.dashboards.find((dashboard) => dashboard.tenantId === tenantId)?.generatedAt ??
          new Date(0).toISOString();
        return dataConfidenceResponseSchema.parse({
          view: "data-confidence",
          schemaVersion: "1.0",
          tenantId,
          generatedAt,
          dataAsOf: generatedAt,
          metrics: (["K073", "K074", "K075", "K076"] as const).map((metricId) => ({
            metricId,
            definitionVersion: "UNACTIVATED",
            definitionStatus: "UNAVAILABLE",
            formulaAuthority: "UNAVAILABLE",
            label: metricId,
            status: "UNAVAILABLE",
            value: null,
            displayValue: "Unavailable",
            dataState: "UNAVAILABLE",
            authorityClass: "UNAVAILABLE",
            productionUseProhibited: false,
            sourceSummary:
              "The browser fixture client does not simulate canonical Data Confidence facts.",
            freshness: "UNKNOWN",
            quality: "INSUFFICIENT",
            reconciliation: "UNKNOWN",
            permissions: ["integration.read", "metric.read"],
            provenance: [],
            unavailableReason:
              "The browser fixture client does not simulate canonical Data Confidence facts.",
          })),
          warnings: [
            {
              code: "UNAVAILABLE_METRICS",
              message: "Fixture mode does not provide Data Confidence values.",
              metricIds: ["K073", "K074", "K075", "K076"],
            },
          ],
        });
      },
    },
    top5: {
      async get({ tenantId }) {
        requireTenant(dataset, tenantId);
        const generatedAt =
          dataset.dashboards.find((dashboard) => dashboard.tenantId === tenantId)?.generatedAt ??
          new Date(0).toISOString();
        const category = (
          key: "problems" | "components" | "products" | "suppliers" | "failure_modes",
          label: string,
        ) => ({
          widgetId: `TOP5-${key.toUpperCase()}`,
          schemaVersion: "1.0",
          definitionVersion: "1",
          asOf: generatedAt,
          dataState: "UNAVAILABLE" as const,
          authorityClass: "UNAVAILABLE",
          sourceSummary: "No governed ranked signal is available in this client fixture.",
          freshness: "UNKNOWN" as const,
          quality: "INSUFFICIENT" as const,
          reconciliation: "UNKNOWN" as const,
          permissions: ["metric.read"],
          payload: {
            category: key,
            label,
            items: [],
            unavailableReason: "The fixture client does not simulate governed Top 5 rankings.",
          },
        });
        return top5ResponseSchema.parse({
          view: "top5",
          schemaVersion: "1.0",
          tenantId,
          generatedAt,
          dataAsOf: generatedAt,
          widgets: [
            category("problems", "Problems"),
            category("components", "Components"),
            category("products", "Products / vehicle lines"),
            category("suppliers", "Suppliers"),
            category("failure_modes", "Failure modes"),
          ],
          warnings: [],
        });
      },
    },
    safetyReview: {
      async get({ tenantId }) {
        requireTenant(dataset, tenantId);
        const generatedAt =
          dataset.dashboards.find((dashboard) => dashboard.tenantId === tenantId)?.generatedAt ??
          new Date(0).toISOString();
        const category = (
          key: "warranty_signals" | "field_actions" | "regulatory_evidence" | "campaign_completion",
          label: string,
          reason: string,
        ) => ({
          widgetId: "SAFETY-" + key.toUpperCase(),
          schemaVersion: "1.0",
          definitionVersion: "1",
          asOf: generatedAt,
          dataState: "UNAVAILABLE" as const,
          authorityClass: "UNAVAILABLE",
          sourceSummary: reason,
          freshness: "UNKNOWN" as const,
          quality: "INSUFFICIENT" as const,
          reconciliation: "UNKNOWN" as const,
          permissions: ["safety_signal.read", "metric.read"],
          payload: {
            category: key,
            label,
            description: reason,
            items: [],
            unavailableReason: reason,
          },
        });
        return safetyReviewResponseSchema.parse({
          view: "safety-review",
          schemaVersion: "1.0",
          tenantId,
          generatedAt,
          dataAsOf: generatedAt,
          widgets: [
            category(
              "warranty_signals",
              "Warranty signal review",
              "The fixture client does not simulate governed canonical warranty signals.",
            ),
            category(
              "field_actions",
              "Field actions",
              "No canonical field-action source is connected.",
            ),
            category(
              "regulatory_evidence",
              "Regulatory evidence",
              "No operating regulatory connector is configured.",
            ),
            category(
              "campaign_completion",
              "Campaign completion",
              "No canonical campaign-completion source is connected.",
            ),
          ],
          warnings: [],
        });
      },
    },
    problemRecords: {
      async list({ tenantId }) {
        requireTenant(dataset, tenantId);
        return dataset.problemRecords.filter((record) => record.tenantId === tenantId);
      },
      async get({ tenantId, id }) {
        requireTenant(dataset, tenantId);
        const result = dataset.problemRecords.find(
          (record) => record.tenantId === tenantId && record.id === id,
        );
        if (!result) {
          throw clientError(404, "NOT_FOUND", "The requested Problem Record is unavailable.");
        }
        return result;
      },
    },
  };
}

/**
 * Fixture mode is intentionally the default. Live mode is an explicit read-only
 * opt-in and never silently falls back to synthetic data after a live failure.
 */
export function createBroadviewClient(options: BroadviewClientOptions = {}): BroadviewApiClient {
  const mode = options.mode ?? "fixture";
  if (mode === "live") {
    return createLiveClient(options);
  }
  return createFixtureClient(options.fixtures ?? createBroadviewFixtureDataset());
}
